import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useLocale } from '../contexts/LocaleContext'
import { getAffiliateLink, getAffiliateLogoPath, getAffiliateName, getBaseSlugFromUrl, getProfileImagePaths } from '../config/site'
import citiesData from '../data/cities.json'
import './Popup.css'

const Popup = () => {
  const location = useLocation()
  const { t } = useLocale()
  const [show, setShow] = useState(false)
  const [showSecondPopup, setShowSecondPopup] = useState(false)
  const [cityName, setCityName] = useState(null)
  const [onlineCount, setOnlineCount] = useState(0)

  // Generate dynamic number between 1000-2000
  useEffect(() => {
    const min = 1000
    const max = 2000
    const count = Math.floor(Math.random() * (max - min + 1)) + min
    setOnlineCount(count)
  }, [])

  useEffect(() => {
    // Check if we're on a city page
    const pathSlug = location.pathname.split('/').pop()
    const baseSlug = getBaseSlugFromUrl(pathSlug)
    const city = citiesData.find(c => c.Slug.toLowerCase() === baseSlug?.toLowerCase())
    if (city) {
      setCityName(city.City)
    } else {
      setCityName(null)
    }
  }, [location])

  useEffect(() => {
    // Show popup on every page change (not just once per session)
    console.log('Popup timer started - will show in 5 seconds')
    
    // Show popup exactly 5 seconds after page change
    const timer = setTimeout(() => {
      console.log('Showing popup now!')
      setShow(true)
    }, 5000) // 5000ms = 5 seconds

    // Cleanup timer on unmount or location change
    return () => {
      clearTimeout(timer)
      setShow(false)
      setShowSecondPopup(false)
    }
  }, [location]) // Show popup on every location change

  // Handle page leave attempts - show popup when user tries to close tab/window
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Show second popup immediately if it's not already shown
      if (!showSecondPopup) {
        // Prevent default browser dialog and show our popup instead
        e.preventDefault()
        e.returnValue = '' // Required for Chrome
        setShowSecondPopup(true)
        setShow(false) // Hide first popup if it's showing
      }
      // Return empty string to trigger browser's confirmation dialog as fallback
      return ''
    }

    const handleVisibilityChange = () => {
      // When tab becomes hidden (user switching tabs, closing tab, or minimizing window)
      if (document.hidden && !showSecondPopup) {
        // Show second popup immediately when tab becomes hidden
        setShowSecondPopup(true)
        setShow(false) // Hide first popup if it's showing
      }
    }

    // Detect when mouse leaves the window (moving towards close button)
    const handleMouseLeave = (e) => {
      // If mouse is leaving through the top of the window, likely going to close button
      if (e.clientY <= 0 && !showSecondPopup) {
        setShowSecondPopup(true)
        setShow(false)
      }
    }

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    document.addEventListener('mouseleave', handleMouseLeave)

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      document.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [show, showSecondPopup]) // Depend on popup states

  const handleClose = () => {
    // When user tries to close, show second popup instead
    setShow(false)
    setShowSecondPopup(true)
  }

  const handleSecondPopupClose = () => {
    // Allow closing the second popup
    setShowSecondPopup(false)
  }

  const handleCTA = () => {
    window.open(getAffiliateLink(), '_blank', 'noopener,noreferrer')
    setShow(false)
    setShowSecondPopup(false)
  }

  const handleSecondPopupCTA = () => {
    window.open(getAffiliateLink(), '_blank', 'noopener,noreferrer')
    setShowSecondPopup(false)
  }

  const siteName = getAffiliateName() || t('common.siteName')
  const title = cityName 
    ? t('cta.popup.titleWithCity', { city: cityName })
    : t('cta.popup.title')
  const message = t('cta.popup.message', { siteName })
  
  // Profile images (1.jpg through 8.jpg) from niche folder
  const profileImages = getProfileImagePaths(8)

  // Second popup content
  const secondPopupTitle = t('cta.popup.second.title')
  const secondPopupMessage = t('cta.popup.second.message', { siteName })
  const secondPopupReassurance = t('cta.popup.second.reassurance')

  return (
    <>
      {/* First Popup */}
      {show && (
        <div className="popup-overlay" onClick={handleClose}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={handleClose} aria-label={t('cta.popup.close')}>
              ×
            </button>
            <div className="popup-body">
              {/* Logo and Brand */}
              <div className="popup-logo-section">
                <img 
                  src={getAffiliateLogoPath()} 
                  alt={siteName}
                  className="popup-logo"
                />
                <span className="popup-brand-name">{siteName}</span>
              </div>

              <h2 className="popup-title">{title}</h2>
              
              {/* Profile Images Section */}
              <div className="popup-profiles-section">
                <div className="popup-profiles-grid">
                  {profileImages.map((img, index) => (
                    <div key={index} className="popup-profile-wrapper">
                      <img 
                        src={img} 
                        alt={`Profile ${index + 1}`}
                        className="popup-profile-image"
                        loading="lazy"
                      />
                      <span className="popup-online-indicator"></span>
                    </div>
                  ))}
                </div>
                <p className="popup-online-count">
                  {t('cta.popup.onlineCount', { count: onlineCount.toLocaleString('de-DE'), AFFILIATE: siteName })}
                </p>
              </div>
              
              <p className="popup-message">{message}</p>
              <button className="popup-cta-button" onClick={handleCTA}>
                {t('cta.popup.button')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Second Popup (shown when user tries to close first popup) */}
      {showSecondPopup && (
        <div className="popup-overlay" onClick={handleSecondPopupClose}>
          <div className="popup-content popup-content-second" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={handleSecondPopupClose} aria-label={t('cta.popup.close')}>
              ×
            </button>
            <div className="popup-body">
              {/* Logo and Brand */}
              <div className="popup-logo-section">
                <img 
                  src={getAffiliateLogoPath()} 
                  alt={siteName}
                  className="popup-logo"
                />
                <span className="popup-brand-name">{siteName}</span>
              </div>

              <h2 className="popup-title">{secondPopupTitle}</h2>
              
              {/* Profile Images Section */}
              <div className="popup-profiles-section">
                <div className="popup-profiles-grid">
                  {profileImages.map((img, index) => (
                    <div key={index} className="popup-profile-wrapper">
                      <img 
                        src={img} 
                        alt={`Profile ${index + 1}`}
                        className="popup-profile-image"
                        loading="lazy"
                      />
                      <span className="popup-online-indicator"></span>
                    </div>
                  ))}
                </div>
                <p className="popup-online-count">
                  {t('cta.popup.onlineCount', { count: onlineCount.toLocaleString('de-DE'), AFFILIATE: siteName })}
                </p>
              </div>
              
              <div className="popup-reassurance-section">
                <p className="popup-message">{secondPopupMessage}</p>
                <p className="popup-reassurance">{secondPopupReassurance}</p>
              </div>
              
              <div className="popup-features">
                <div className="popup-feature-item">
                  <span className="popup-feature-icon">✓</span>
                  <span>{t('cta.popup.second.feature1')}</span>
                </div>
                <div className="popup-feature-item">
                  <span className="popup-feature-icon">✓</span>
                  <span>{t('cta.popup.second.feature2')}</span>
                </div>
                <div className="popup-feature-item">
                  <span className="popup-feature-icon">✓</span>
                  <span>{t('cta.popup.second.feature3')}</span>
                </div>
              </div>
              
              <button className="popup-cta-button" onClick={handleSecondPopupCTA}>
                {t('cta.popup.second.button')}
              </button>
              <button className="popup-secondary-button" onClick={handleSecondPopupClose}>
                {t('cta.popup.second.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Popup

